// =========================================================================
// HelpyDoc Metabolic · Contrato de la ENTRADA DEL MODELO
// =========================================================================
// La forma de lo que el cliente calcula antes de llamar al modelo y que viaja
// con el trabajo de análisis: el veredicto del portero de calidad, la geometría
// del plato y el estimador previo.
//
// VERSIONADO. `VERSION_PREPARADOR` sella cada estimación. No es decorativo: la
// entrada del modelo NO se guarda (ADR-016 §D4), y lo que la hace reproducible
// —y por tanto auditable— es la terna «foto original + rectángulo de recorte +
// versión del preparador». Si el algoritmo cambia y la versión no, se pierde la
// capacidad de reconstruir qué vio el modelo.
//
// LÍMITE DE COMPETENCIA. Nada de lo que hay aquí es una medida clínica ni
// pretende serlo: es geometría sobre píxeles, con una incertidumbre declarada y
// ancha. Se usa para ANCLAR la escala del modelo y para contrastar su
// plausibilidad, nunca para sustituir su cifra (ADR-016 §D2).
// =========================================================================

/** Sella el algoritmo que produjo una estimación. Súbela al cambiarlo. */
export const VERSION_PREPARADOR = 'entrada-modelo.v1';

/**
 * Diámetro de referencia del plato llano estándar, en centímetros.
 *
 * Es EL MISMO valor que el prompt del orquestador da al modelo como regla de
 * medir («Plato llano estándar: ⌀ 26-27 cm»). Tiene que serlo: si el cliente
 * calibrara con 24 y el prompt dijera 26, se le estaría dando al modelo una
 * escala y una referencia que no encajan, y el error sería sistemático y mudo.
 */
export const DIAMETRO_PLATO_CM = 26.5;

/** Veredicto del portero de calidad. Tres estados y ninguno más. */
export type Veredicto = 'apto' | 'avisa' | 'rechaza';

/** Motivo legible de un aviso o un rechazo, con su código estable. */
export interface MotivoCalidad {
  codigo:
    | 'BORROSA'
    | 'MUY_OSCURA'
    | 'SOBREEXPUESTA'
    | 'RESOLUCION_BAJA'
    | 'RESOLUCION_INSUFICIENTE'
    | 'ILEGIBLE'
    | 'SIN_PLATO'
    | 'PLATO_CORTADO';
  /** Qué se le dice al paciente. En segunda persona y accionable. */
  mensaje: string;
}

export interface InformeCalidad {
  veredicto: Veredicto;
  motivos: MotivoCalidad[];
  /** Medidas crudas, para poder revisar una decisión sin repetir la foto. */
  medidas: {
    nitidez: number;
    recorteOscuro: number;
    recorteClaro: number;
    lumaMedia: number;
    ladoMenorPx: number;
  };
}

/** Geometría del plato detectado, en píxeles de la imagen ORIGINAL. */
export interface PlatoDetectado {
  centroX: number;
  centroY: number;
  /** Radio medio, en píxeles de la imagen original. */
  radioPx: number;
  /** Caja que lo contiene, en píxeles de la imagen original. */
  bbox: { x: number; y: number; ancho: number; alto: number };
  /** 0–1. Por debajo del umbral no se recorta ni se estima. */
  confianza: number;
  /** ¿Cabe entero en el fotograma? Si no, no se puede usar como regla. */
  completo: boolean;
  /** Fracción del plato cubierta por comida (0–1). */
  coberturaComida: number;
}

/**
 * El estimador previo: un PRIOR, no una corrección.
 *
 * Se emite solo cuando hay plato fiable. Si no lo hay, no hay estimación —
 * inventar una escala a partir de nada sería peor que no dar ninguna, porque
 * el modelo la usaría igual.
 */
export interface EstimacionPrevia {
  version: string;
  /** Píxeles por centímetro, derivados del diámetro del plato. */
  pxPorCm: number;
  /** Superficie de comida visible, en cm². */
  areaComidaCm2: number;
  /** Prior de masa total del plato, en gramos, con su rango. */
  gramosPrior: number;
  gramosRango: { bajo: number; alto: number };
  /** 0–1. Baja a propósito: es geometría, no una medida. */
  confianza: number;
  plato: PlatoDetectado;
}

/** La imagen que se le da al modelo, ya recortada y reescalada. */
export interface EntradaModelo {
  /** Data URL de la imagen preparada. */
  dataUrl: string;
  contentType: string;
  anchoPx: number;
  altoPx: number;
  bytes: number;
  /** Rectángulo recortado sobre la imagen ORIGINAL. Sin recorte: el completo. */
  recorte: { x: number; y: number; ancho: number; alto: number };
  /** ¿Se llegó a recortar? `false` cuando la guarda lo impidió. */
  recortada: boolean;
  version: string;
}

/** Todo lo que el cliente prepara antes de llamar al modelo. */
export interface PreparacionEntrada {
  calidad: InformeCalidad;
  /** Ausente cuando no hubo plato fiable. */
  estimacionPrevia?: EstimacionPrevia;
  /** Ausente cuando el navegador no pudo preparar la imagen. */
  entrada?: EntradaModelo;
}

/**
 * Lo que de verdad viaja al backend, ya aplanado y acotado.
 *
 * Se declara aparte del tipo de trabajo interno porque cruza un límite de
 * proceso: aquí se decide qué sale del navegador, y la respuesta es «lo
 * mínimo que el modelo necesita y la auditoría tiene que poder reconstruir»,
 * no «el objeto que resultó cómodo».
 */
export interface EstimacionPreviaTransmitida {
  version: string;
  pxPorCm: number;
  areaComidaCm2: number;
  gramosPrior: number;
  gramosBajo: number;
  gramosAlto: number;
  confianza: number;
  platoDiametroPx: number;
  platoCoberturaComida: number;
  recorte?: { x: number; y: number; ancho: number; alto: number };
}

export function aTransmitida(
  e: EstimacionPrevia,
  recorte?: { x: number; y: number; ancho: number; alto: number },
): EstimacionPreviaTransmitida {
  const r2 = (n: number) => Math.round(n * 100) / 100;
  return {
    version: e.version,
    pxPorCm: r2(e.pxPorCm),
    areaComidaCm2: r2(e.areaComidaCm2),
    gramosPrior: Math.round(e.gramosPrior),
    gramosBajo: Math.round(e.gramosRango.bajo),
    gramosAlto: Math.round(e.gramosRango.alto),
    confianza: r2(e.confianza),
    platoDiametroPx: Math.round(e.plato.radioPx * 2),
    platoCoberturaComida: r2(e.plato.coberturaComida),
    ...(recorte ? { recorte } : {}),
  };
}

/**
 * Tope del data URL que se manda al backend, en caracteres.
 *
 * DELIBERADAMENTE MENOR que el del backend (512 000, en
 * `shared/messaging/job-contracts.ts`). La asimetría es la defensa: si las dos
 * cifras se desincronizan, el cliente será el estricto y simplemente dejará de
 * mandar la imagen preparada —el análisis seguirá funcionando con la foto de
 * Storage—. Al revés, un cliente más permisivo que el servidor haría que el
 * backend rechazara la petición entera y el análisis fallara.
 */
export const MAX_ENTRADA_CHARS = 480_000;

/** Lo que de verdad se transmite del recorte. `null` si no debe mandarse. */
export interface EntradaModeloTransmitida {
  dataUrl: string;
  contentType: string;
  anchoPx: number;
  altoPx: number;
  recortada: boolean;
  version: string;
}

/**
 * Aplana la entrada a los campos EXACTOS que acepta el backend.
 *
 * Se enumeran a mano y no se hace `...entrada`: el esquema del backend es
 * `.strict()`, así que un campo de más —`bytes`, `recorte`— haría que la
 * petición entera se rechazara con un 400 y el paciente se quedaría sin
 * análisis por un dato que ni siquiera hacía falta mandar.
 */
export function aEntradaTransmitida(
  entrada: EntradaModelo | undefined,
): EntradaModeloTransmitida | undefined {
  if (!entrada) return undefined;
  if (entrada.dataUrl.length > MAX_ENTRADA_CHARS) return undefined;
  return {
    dataUrl: entrada.dataUrl,
    contentType: entrada.contentType,
    anchoPx: entrada.anchoPx,
    altoPx: entrada.altoPx,
    recortada: entrada.recortada,
    version: entrada.version,
  };
}

/**
 * Los avisos del portero, en el formato de `warnings` del resultado.
 *
 * Van al MISMO sitio que los avisos del modelo a propósito: al paciente le da
 * igual quién notó que la foto salió movida; lo que necesita es saber que la
 * cifra que está viendo se calculó sobre una foto mejorable.
 */
export function avisosDeCalidad(calidad: InformeCalidad): string[] {
  if (calidad.veredicto !== 'avisa') return [];
  return calidad.motivos.map((m) => m.mensaje);
}

/** El mensaje que se le da al paciente cuando la foto no es analizable. */
export function mensajeDeRechazo(calidad: InformeCalidad): string {
  const primero = calidad.motivos.find((m) => m.codigo === 'ILEGIBLE' || m.codigo === 'RESOLUCION_INSUFICIENTE');
  return primero?.mensaje ?? 'La foto no se puede analizar. Repítela con el plato bien encuadrado y con luz.';
}
